import { Component, Input, OnInit, inject } from '@angular/core'
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { BaseErrorStateMatcher } from '@core/matchers'
import { LocationService } from '@app/features/crm/services/location.service'
import { ELocationType } from '@app/features/crm/models/location.model'
import { STATE_OPTIONS } from '@core/constants/client.constants'
import { catchError } from 'rxjs/operators'
import { of } from 'rxjs'
import {
    ZardFormFieldComponent,
    ZardFormLabelComponent,
    ZardFormControlComponent,
    ZardFormMessageComponent,
} from '../../../../styles/components/form/form.component'
import { ZardInputDirective } from '../../../../styles/components/input/input.directive'
import {
    TwAutocompleteComponent,
    TwAutocompleteOptionComponent,
} from '../tw-autocomplete/tw-autocomplete.component'
import { ZardLoaderComponent } from '../../../../styles/components/loader/loader.component'

/**
 * Reusable component for State and District dropdown fields
 * Handles state filtering and district loading from API
 */
@Component({
    selector: 'app-state-district-fields',
    templateUrl: './state-district-fields.component.html',
    styleUrls: ['./state-district-fields.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ZardFormFieldComponent,
        ZardFormLabelComponent,
        ZardFormControlComponent,
        ZardInputDirective,
        TwAutocompleteComponent,
        TwAutocompleteOptionComponent,
        ZardFormMessageComponent,
        ZardLoaderComponent,
    ],
})
export class StateDistrictFieldsComponent implements OnInit {
    @Input() formGroup!: FormGroup
    @Input() stateControlName: ELocationType = ELocationType.STATE
    @Input() districtControlName: ELocationType = ELocationType.DISTRICT
    @Input() stateLabel = 'State'
    @Input() districtLabel = 'District'
    @Input() isRequired = true
    @Input() errorStateMatcher!: BaseErrorStateMatcher

    public readonly stateOptions = STATE_OPTIONS
    public filteredStateOptions: string[] = STATE_OPTIONS
    public districtOptions: string[] = []
    public isDistrictLoading = false

    private locationService = inject(LocationService)

    ngOnInit(): void {
        // Initialize district options if state is already selected
        const selectedState = this.formGroup.get(this.stateControlName)?.value
        if (selectedState) {
            this.loadDistrictsByState(selectedState)
        }
    }

    onStateInput(event: Event): void {
        const searchValue = (event.target as HTMLInputElement).value.toLowerCase()
        this.filteredStateOptions = STATE_OPTIONS.filter((state) =>
            state.toLowerCase().includes(searchValue),
        )
    }

    onStateChange(): void {
        const selectedState = this.formGroup.get(this.stateControlName)?.value
        const districtControl = this.formGroup.get(this.districtControlName)

        // Reset district when state changes
        if (districtControl) {
            districtControl.setValue('')
        }

        // Load districts for the new state
        if (selectedState) {
            this.loadDistrictsByState(selectedState)
        } else {
            this.districtOptions = []
        }
    }

    private loadDistrictsByState(stateName: string): void {
        if (!stateName) {
            this.districtOptions = []
            return
        }

        this.isDistrictLoading = true
        this.districtOptions = []

        this.locationService
            .getDistrictsByState(stateName)
            .pipe(catchError(() => of([])))
            .subscribe({
                next: (districts) => {
                    this.districtOptions = districts
                    this.isDistrictLoading = false
                },
                error: () => {
                    this.districtOptions = []
                    this.isDistrictLoading = false
                },
            })
    }
}
